import type { RootState } from "@/app/store";
import { DetailedSurvey } from "@/features/survey/types/survey.core.types";
import { updateCurrentSurvey } from "@features/survey/surveySlice";
import type { SurveySection } from "@survey-types/survey.section.types";
import { surveyApi } from "./base";
import { surveyCoreApi } from "./survey-core";

// Survey Sections API - handles section management and media
export const surveySectionsApi = surveyApi
  .enhanceEndpoints({
    addTagTypes: ["Survey", "LeaseComps"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      reorderSections: build.mutation<
        DetailedSurvey,
        { surveyId: string; order: number; surveySectionId: string }
      >({
        query: ({ surveyId, order, surveySectionId }) => ({
          url: `/section/reorder`,
          method: "PATCH",
          body: { surveyId, order, surveySectionId },
        }),
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "Survey", id: surveyId },
          { type: "LeaseComps", id: surveyId },
        ],
      }),
      updateSection: build.mutation<
        SurveySection,
        {
          sectionId: string;
          updates: Partial<{ isHidden: boolean; name: string }>;
        }
      >({
        query: ({ sectionId, updates }) => ({
          url: `/section`,
          method: "PATCH",
          body: {
            surveySectionId: sectionId,
            ...updates,
          },
        }),
        invalidatesTags: (result) => [
          { type: "Survey", id: result?.surveyId },
          { type: "LeaseComps", id: result?.surveyId },
        ],
      }),
      createSection: build.mutation<
        SurveySection,
        { surveyId: string; name: string; isCustom?: boolean }
      >({
        query: ({ surveyId, name, isCustom = true }) => ({
          url: `/section`,
          method: "POST",
          body: { surveyId, name, isCustom },
        }),
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "Survey", id: surveyId },
          { type: "LeaseComps", id: surveyId },
        ],
      }),
      deleteSection: build.mutation<
        void,
        { sectionId: string; surveyId: string }
      >({
        query: ({ sectionId }) => ({
          url: `/section/${sectionId}`,
          method: "DELETE",
        }),
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "Survey", id: surveyId },
          { type: "LeaseComps", id: surveyId },
        ],
      }),
      createSectionMedia: build.mutation<
        {
          surveySectionMediaId: string;
          mediaUrl: string;
          mediaSize: number;
          mediaName: string;
          updatedOn: string;
          order: number;
          createdOn: string;
        },
        {
          surveysectionId: string;
          mediaUrl: string;
          mediaSize: number;
          mediaName: string;
          surveyId: string;
        }
      >({
        query: (body) => ({
          url: `/sectionMedia`,
          method: "POST",
          body,
        }),
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "Survey", id: surveyId },
          { type: "LeaseComps", id: surveyId },
        ],
      }),
      updatePdfSection: build.mutation<
        void,
        {
          surveySectionMediaId: string;
          mediaName: string;
          surveyId: string;
        }
      >({
        query: (body) => ({
          url: `/pdfSection`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "Survey", id: surveyId },
          { type: "LeaseComps", id: surveyId },
        ],
      }),
      toggleSectionVisibility: build.mutation<
        SurveySection,
        { sectionId: string; isHidden: boolean }
      >({
        query: ({ sectionId, isHidden }) => ({
          url: `/section`,
          method: "PATCH",
          body: { surveySectionId: sectionId, isHidden },
        }),
        async onQueryStarted(
          { sectionId, isHidden },
          { dispatch, queryFulfilled, getState },
        ) {
          const state = getState() as RootState;
          const surveyId = state.survey.currentSurvey?.id ?? "";
          const sectionsBackup = state.survey.currentSurvey?.sections ?? [];

          // Optimistically update RTK Query cache
          const cachePatch = dispatch(
            surveyCoreApi.util.updateQueryData(
              "getSurvey",
              surveyId,
              (draft) => {
                const section = draft.sections?.find(
                  (s) => s.surveySectionId === sectionId,
                );
                if (section) {
                  section.isHidden = isHidden;
                }
              },
            ),
          );

          // Optimistically update Redux slice
          const updatedSections = sectionsBackup.map((s) =>
            s.surveySectionId === sectionId ? { ...s, isHidden } : s,
          );
          dispatch(updateCurrentSurvey({ sections: updatedSections }));

          try {
            await queryFulfilled;
          } catch (_error) {
            cachePatch.undo();
            dispatch(updateCurrentSurvey({ sections: sectionsBackup }));
            console.error("Error in toggleSectionVisibility");
          }
        },
        // No invalidatesTags — optimistic updates handle all state sync
      }),
      deletePdfSection: build.mutation<
        void,
        {
          surveySectionMediaId: string;
          surveyId: string;
        }
      >({
        query: ({ surveySectionMediaId }) => ({
          url: `/pdfSection/${surveySectionMediaId}`,
          method: "DELETE",
          // The backend returns a plain text body (e.g. "deleted") with
          // Content-Type: application/json. Parsing it as JSON throws a
          // PARSING_ERROR which RTK Query treats as a failed mutation even
          // on 200 OK. Use "text" to avoid that.
          responseHandler: "text" as const,
        }),
        // Disable automatic retries — the global base query retries up to 3
        // times on failure. Retrying a DELETE on an already-removed resource
        // causes the backend to 500 (NullPointerException on missing section).
        extraOptions: { maxRetries: 0 },
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "Survey", id: surveyId },
          { type: "LeaseComps", id: surveyId },
        ],
      }),
    }),
  });

export const {
  useReorderSectionsMutation,
  useUpdateSectionMutation,
  useToggleSectionVisibilityMutation,
  useCreateSectionMutation,
  useDeleteSectionMutation,
  useCreateSectionMediaMutation,
  useUpdatePdfSectionMutation,
  useDeletePdfSectionMutation,
} = surveySectionsApi;
